import type { Customer } from '@clockwise/clients/customers';
import type { Product } from '@clockwise/clients/products';
import type { TimerView } from '@clockwise/clients/timers';
import {
  type EngagementCandidate,
  type EngagementResolution,
  findCoveringMemberships,
  isCommittableElapsed,
} from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { Check, CheckCheck, Loader2, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CustomerAutocomplete } from '@/components/custom/customer-autocomplete';
import { useEngagementRequiredPrompt } from '@/components/custom/engagement-required-prompt';
import { ProductAutocomplete } from '@/components/custom/product-autocomplete';
import { PulsingDot } from '@/components/custom/pulsing-dot';
import { isActivityVisibleForCustomer } from '@/components/custom/timesheet-activity-visibility';
import { BillableSwitch } from '@/components/custom/timesheet-billable';
import { TimesheetEngagementDialog } from '@/components/custom/timesheet-engagement-dialog';
import {
  engagementCandidatesForRow,
  engagementsActiveForRow,
  resolveRowEngagement,
} from '@/components/custom/timesheet-row-engagement';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useEngagements } from '@/hooks/use-engagements';
import { useInScopeProductsByCustomer } from '@/hooks/use-in-scope-products';
import { useRecentProductsByCustomer } from '@/hooks/use-recent-products';
import { customersApi, customerTeamApi, productsApi } from '@/lib/api';
import { engagementRequiredCandidates } from '@/lib/engagement-required';
import { useMe } from '@/lib/me';
import { SHOW_OUT_OF_SCOPE } from '@/lib/out-of-scope';
import { queryKeys } from '@/lib/query-keys';
import {
  formatDurationShort,
  formatElapsed,
  isTempTimerId,
  useCommitTimer,
  useCommitTimers,
  useCustomerNameLookup,
  useDiscardTimer,
  useLiveElapsed,
  usePatchTimer,
  usePauseTimer,
  useProductNameLookup,
  useResumeTimer,
  useStartTimer,
  useTimers,
  useTimersFetchedAt,
} from '@/lib/timer-hooks';
import { cn, localTodayIso } from '@/lib/utils';

/**
 * Shared required-field-guard state for a timer card: customer, activity, and
 * notes are all required to commit, and the commit button + the three fields
 * live in sibling components, so the per-field "blocked commit" error flags
 * live here and thread into both. Each error is only ever set by a blocked
 * commit attempt (see `TimerActions.commitTimer`) — the fields carry no
 * default/soft highlighting while empty, only the red error state once a
 * commit has actually been attempted with that field missing.
 *
 * Takes the current field values directly (rather than a `TimerView`) so it
 * works both for an existing timer (patched optimistically — `customerId`/
 * `productId` are already race-free at commit time, no live ref needed for
 * those two, unlike notes) and for `DraftTimerCard`'s not-yet-started draft
 * (local state, no patching at all). Notes is the one field that's only committed
 * on blur, so `liveDescriptionRef` mirrors every keystroke (updated
 * independent of blur/commit) and both the commit handler and the draft's
 * start handler read that instead of `description`, which can be a render
 * behind the blur/state-update that just saved it. The effect keeps the ref
 * correct even when the form is unmounted (a collapsed paused card) or the
 * description changes externally (activity auto-fill, a refetch) — and
 * clears the notes error once the stored value is non-empty again,
 * mirroring the customer/activity effects below.
 */
function useRequiredFieldsGuard(
  customerId: string | null,
  productId: string | null,
  description: string | null,
  engagementId: string | null,
) {
  const [customerError, setCustomerError] = useState(false);
  const [activityError, setActivityError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  // Engagement is required only when several cover the timer (see
  // `useTimerEngagement`). Its prompt's open state lives here, beside the
  // error, because the action that finds it missing (Start / Commit) and the
  // form that hosts the prompt are siblings.
  const [engagementError, setEngagementError] = useState(false);
  const [engagementPromptOpen, setEngagementPromptOpen] = useState(false);
  const liveDescriptionRef = useRef(description ?? '');
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (customerId != null) setCustomerError(false);
  }, [customerId]);
  useEffect(() => {
    if (productId != null) setActivityError(false);
  }, [productId]);
  useEffect(() => {
    if (engagementId != null) setEngagementError(false);
  }, [engagementId]);
  useEffect(() => {
    liveDescriptionRef.current = description ?? '';
    if ((description ?? '').trim() !== '') setDescriptionError(false);
  }, [description]);
  return {
    customerError,
    setCustomerError,
    activityError,
    setActivityError,
    descriptionError,
    setDescriptionError,
    engagementError,
    setEngagementError,
    engagementPromptOpen,
    setEngagementPromptOpen,
    liveDescriptionRef,
    descriptionInputRef,
  };
}

type RequiredFieldsGuard = ReturnType<typeof useRequiredFieldsGuard>;

/** Which required field(s) are unset — customer, activity, and notes are all
 *  required both to start a new timer and to commit an existing one. */
type MissingField = 'customer' | 'activity' | 'notes' | 'engagement';

/** Which of the three required fields are currently unset, given the live
 *  (not-yet-blurred) description — shared by `DraftTimerCard`'s Start and
 *  `TimerActions`' commit, the two places that gate an action on all three
 *  being filled in. */
function computeMissingFields(
  customerId: string | null,
  productId: string | null,
  liveDescription: string,
  /** Several engagements cover the timer and none is picked. */
  needsEngagement: boolean,
): MissingField[] {
  const missing: MissingField[] = [];
  if (customerId == null) missing.push('customer');
  if (productId == null) missing.push('activity');
  if (liveDescription.trim() === '') missing.push('notes');
  if (needsEngagement) missing.push('engagement');
  return missing;
}

/**
 * Flags each missing field's error state on the guard, and focuses notes if
 * that's one of them — shared by `DraftTimerCard`'s Start button and
 * `RunningTimerCard`/`PausedTimerCard`'s commit-blocked callback.
 * `deferFocus` waits a frame before focusing: `PausedTimerCard` reveals its
 * (collapsed-by-default) form in the same tick, so the textarea isn't
 * mounted yet when a plain synchronous focus would fire.
 */
function applyMissingFieldErrors(
  guard: RequiredFieldsGuard,
  missing: readonly MissingField[],
  opts: { deferFocus?: boolean } = {},
) {
  if (missing.includes('customer')) guard.setCustomerError(true);
  if (missing.includes('activity')) guard.setActivityError(true);
  if (missing.includes('engagement')) {
    // Ask straight away — the only way to fill this field is to answer.
    guard.setEngagementError(true);
    guard.setEngagementPromptOpen(true);
  }
  if (missing.includes('notes')) {
    guard.setDescriptionError(true);
    if (opts.deferFocus) {
      requestAnimationFrame(() => guard.descriptionInputRef.current?.focus());
    } else {
      guard.descriptionInputRef.current?.focus();
    }
  }
}

/**
 * The engagement rule applied to a timer — the same one a timesheet entry
 * follows (`@clockwise/shared`), on the timer's single work date. Called by
 * each CARD rather than inside `TimerForm`, because Start and Commit have to
 * know whether an engagement is still owed even when the form isn't mounted
 * (a collapsed paused card).
 *
 * Only the ambiguous case asks anything of the user: one covering engagement
 * is bound by the server when the timer is committed, and none leaves the
 * entry unattributed.
 */
function useTimerEngagement(
  values: Pick<TimerFieldValues, 'customerId' | 'productId' | 'engagementId'>,
  workDate: string,
) {
  const { user } = useMe();
  const { versions, isLoaded } = useEngagements(user?.id, workDate, workDate);
  const dates = [workDate];
  const resolve = (
    customerId: string | null,
    productId: string | null,
    engagementId: string | null,
  ): EngagementResolution =>
    isLoaded && workDate
      ? resolveRowEngagement(versions, { customerId, productId, engagementId, dates }, workDate)
      : { kind: 'pending' };
  const candidatesFor = (
    customerId: string | null,
    productId: string | null,
  ): EngagementCandidate[] =>
    isLoaded && workDate
      ? engagementCandidatesForRow(versions, { customerId, productId, dates }, workDate)
      : [];
  const activeFor = (customerId: string | null): EngagementCandidate[] =>
    isLoaded && workDate ? engagementsActiveForRow(versions, { customerId, dates }, workDate) : [];
  const resolution = resolve(values.customerId, values.productId, values.engagementId);
  return {
    resolution,
    /** Several engagements cover the timer and none is picked. */
    needsPick: resolution.kind === 'ask',
    resolve,
    candidatesFor,
    activeFor,
  };
}

type TimerEngagement = ReturnType<typeof useTimerEngagement>;

// Stable empty fallback so the customer picker's `items` keeps a constant
// identity while the team-customers query is loading/disabled.
const EMPTY_CUSTOMERS: readonly Customer[] = [];

// Border applied to a required field (Customer/Activity/Notes) once a commit
// attempt has actually blocked on it — fields carry no highlighting while
// simply empty, only after a blocked commit.
const MISSING_FIELD_ERROR_CLASSNAME =
  'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30';

/**
 * The "Out of scope" badge (and the In/Out scope sectioning it drives) on the
 * timer's activity picker — off until out-of-scope labelling is released. One
 * switch for every surface: `SHOW_OUT_OF_SCOPE`.
 */
const SHOW_OUT_OF_SCOPE_BADGE = SHOW_OUT_OF_SCOPE;

/**
 * Body of the timer dropdown. Header shows total open-count, a
 * "Commit all" bulk action (when 2+ are eligible), and a "+ New"
 * button. Body lists the running timer (if any) followed by paused
 * timers; cards use compact icon-only action buttons with tooltips.
 *
 * `onTimerStarted` fires once either `DraftTimerCard` (the empty-state one
 * or the header-"+"-triggered one) actually starts a timer — `TimerHud`
 * uses it to close the popover so the user isn't left staring at the timer
 * they just started.
 */
export function TimerPopover({ onTimerStarted }: { onTimerStarted?: () => void } = {}) {
  // The header "+" doesn't start a timer itself — it just reveals a second
  // `DraftTimerCard` above the existing running/paused list (Cancel hides it
  // again), so adding another timer while one's already open goes through
  // the exact same customer/activity/notes gate as the very first one.
  const [addingDraft, setAddingDraft] = useState(false);
  // The id of whatever was running when "+" was clicked, if anything — kept
  // so Cancel can resume exactly that timer. By the time Cancel is clicked,
  // `running` (below) is already null (see `pauseForDraft` below), so it
  // can't itself answer "what should resume".
  const [pausedForDraftId, setPausedForDraftId] = useState<string | null>(null);
  const { running, paused, pausedTotal, isPending } = useTimers();
  const { permissions } = useMe();
  const canEdit = permissions.has('timer.edit');
  // Pausing "on open" rather than waiting for the new timer's own Start is
  // what the auto-pause-on-start behavior already does server-side, just
  // moved earlier: the user has visibly committed to switching tasks the
  // moment they open the draft, so the old timer shouldn't keep accumulating
  // time while they're still picking the new one's customer/activity/notes.
  const pauseForDraft = usePauseTimer();
  const resumeAfterCancel = useResumeTimer();

  // Total open = (running ? 1 : 0) + paused total (un-truncated count).
  const openTotal = (running ? 1 : 0) + pausedTotal;
  // Combined elapsed across all loaded timers, shown next to the count in
  // the header. The running slice ticks live (same base-plus-wall-clock
  // trick as the running card); paused timers add their static server
  // elapsed. Reflects loaded timers only — if the paused list is truncated
  // (pausedTotal > paused.length) those aren't summed, while `openTotal`
  // still shows the true count.
  const baseAt = useTimersFetchedAt();
  const runningElapsed = useLiveElapsed(running?.elapsedSeconds ?? 0, baseAt, running != null);
  const totalSeconds =
    (running ? runningElapsed : 0) + paused.reduce((sum, v) => sum + v.elapsedSeconds, 0);
  // Eligible-for-commit count drives whether to show the bulk action.
  // A timer is eligible iff it has a customer + product + description set
  // and is not a still-optimistic placeholder. Description is required
  // because the commit always logs hours, and logged time can't be saved
  // without a description — so a description-less timer would only fail the
  // bulk commit. The single-commit button stays available for those (it
  // surfaces the missing-description error in place).
  const eligibleIds = useMemo<string[]>(() => {
    const all: TimerView[] = running ? [running, ...paused] : [...paused];
    return all
      .filter(
        (v) =>
          v.timer.customerId != null &&
          v.timer.productId != null &&
          Boolean(v.timer.description?.trim()),
      )
      .filter((v) => !isTempTimerId(v.timer.id))
      .map((v) => v.timer.id);
  }, [running, paused]);

  // A draft card is already on screen — either because the header "+" was
  // clicked, or because there's nothing open yet and `DraftTimerCard` is the
  // empty-state itself. Either way, showing the "+" too would just offer a
  // second, redundant way to do the same thing.
  const emptyDraftShowing = !isPending && !running && paused.length === 0 && canEdit;
  const draftShowing = addingDraft || emptyDraftShowing;

  return (
    <div className="flex max-h-[70vh] w-[380px] flex-col">
      <div className="flex items-start justify-between gap-2 border-b px-4 py-3">
        <div>
          <div className="font-medium text-sm">Timers</div>
          {!isPending && openTotal > 0 ? (
            <div className="mt-0.5 text-muted-foreground text-xs">
              {openTotal} open · {formatDurationShort(totalSeconds)} logged
            </div>
          ) : null}
        </div>
        {canEdit && !draftShowing ? (
          <div className="flex items-center gap-1">
            {eligibleIds.length >= 2 ? <CommitAllButton ids={eligibleIds} /> : null}
            <NewTimerButton
              onClick={() => {
                if (running) {
                  setPausedForDraftId(running.timer.id);
                  pauseForDraft.mutate(running.timer.id);
                }
                setAddingDraft(true);
              }}
            />
          </div>
        ) : null}
      </div>
      <div className="flex-1 overflow-y-auto">
        {isPending ? (
          <div className="px-4 py-8 text-center text-muted-foreground text-sm">Loading…</div>
        ) : null}
        {addingDraft ? (
          <DraftTimerCard
            onCancel={() => {
              setAddingDraft(false);
              // Changed their mind — put back whatever was running before
              // "+" paused it on open.
              if (pausedForDraftId) {
                resumeAfterCancel.mutate(pausedForDraftId);
                setPausedForDraftId(null);
              }
            }}
            onStarted={() => {
              setAddingDraft(false);
              setPausedForDraftId(null);
              onTimerStarted?.();
            }}
          />
        ) : null}
        {!isPending && !addingDraft && !running && paused.length === 0 ? (
          canEdit ? (
            <DraftTimerCard onStarted={onTimerStarted} />
          ) : (
            <EmptyState />
          )
        ) : null}
        {running ? <RunningTimerCard view={running} canEdit={canEdit} /> : null}
        {running && paused.length > 0 ? (
          <div className="px-4 py-2 text-muted-foreground text-xs uppercase tracking-wide">
            Paused ({paused.length})
          </div>
        ) : null}
        {paused.map((view) => (
          <PausedTimerCard key={view.timer.id} view={view} canEdit={canEdit} />
        ))}
      </div>
    </div>
  );
}

function NewTimerButton({ onClick }: { onClick: () => void }) {
  return (
    <IconTooltip label="New timer">
      <Button
        size="icon"
        variant="ghost"
        onClick={onClick}
        className="size-7"
        aria-label="New timer"
      >
        <Plus />
      </Button>
    </IconTooltip>
  );
}

function CommitAllButton({ ids }: { ids: readonly string[] }) {
  const bulk = useCommitTimers();
  return (
    <IconTooltip label={`Commit all eligible (${ids.length})`}>
      <Button
        size="icon"
        variant="ghost"
        disabled={bulk.isPending}
        onClick={() => bulk.mutate(ids)}
        className="size-7"
        aria-label={`Commit ${ids.length} timers to timesheet`}
      >
        <CheckCheck />
      </Button>
    </IconTooltip>
  );
}

// Read-only fallback (no `timer.edit`) — these users can't fill in
// `DraftTimerCard` anyway, so there's nothing to show but the plain state.
function EmptyState() {
  return (
    <div className="px-4 py-8 text-center">
      <span className="text-muted-foreground text-sm">No open timers.</span>
    </div>
  );
}

// Fresh, unset draft — the starting point every time the popover goes from
// having an open timer to having none (the component remounts, since it
// swaps in/out of the tree with the empty-state condition).
const EMPTY_TIMER_FIELD_VALUES: TimerFieldValues = {
  customerId: null,
  productId: null,
  engagementId: null,
  description: null,
  billable: false,
};

/**
 * Stands in for `RunningTimerCard` before any timer exists: renders the
 * exact same `TimerForm`, just backed by local draft state instead of a
 * real timer to patch, so the popover never shows a bare empty state —
 * only the header's elapsed clock and the footer action differ (a single
 * "Start timer" button in place of Pause/Commit/Discard, since nothing
 * exists yet to pause, commit, or discard). "Start timer" sends the
 * accumulated draft in one `useStartTimer` call — no timer is created just
 * by opening the popover.
 *
 * `onCancel` is only passed when this draft is layered above an existing
 * running/paused list (the header "+" flow) — there's something to fall
 * back to, so a Cancel button appears. The plain empty-state usage omits it:
 * with nothing else in the popover, there's nothing meaningful to cancel to.
 * `onStarted` fires as soon as a validated Start click kicks off the
 * mutation (not from the mutation's own `onSuccess` — see the comment in
 * `onStart`), letting either caller collapse its own "show the draft" state
 * back down.
 */
function DraftTimerCard({
  onCancel,
  onStarted,
}: {
  onCancel?: () => void;
  onStarted?: () => void;
}) {
  const start = useStartTimer();
  const [values, setValues] = useState<TimerFieldValues>(EMPTY_TIMER_FIELD_VALUES);
  const guard = useRequiredFieldsGuard(
    values.customerId,
    values.productId,
    values.description,
    values.engagementId,
  );
  // Approximates "today" for scoping the pickers (team memberships, in-scope
  // and recent products) before any timer — and its server-assigned real
  // workDate — exists. Client-local, same as other pre-timesheet date
  // guesses in this app; the actual workDate is resolved server-side on
  // start.
  const workDate = useMemo(() => localTodayIso(), []);
  const engagement = useTimerEngagement(values, workDate);

  const onStart = () => {
    // Notes may have been typed but not yet blurred into `values.description`
    // — read the live ref instead, same as `TimerActions.commitTimer` does
    // for an existing timer.
    const live = guard.liveDescriptionRef.current;
    // Same required-field guard as commit: customer, activity, and notes are
    // all required, and a click with any missing blocks in place (rather
    // than letting the server 422) and surfaces per-field errors instead of
    // hard-disabling the button.
    const missing = computeMissingFields(
      values.customerId,
      values.productId,
      live,
      engagement.needsPick,
    );
    if (missing.length > 0) {
      applyMissingFieldErrors(guard, missing);
      return;
    }
    start.mutate({
      customerId: values.customerId,
      productId: values.productId,
      engagementId: values.engagementId,
      description: live.trim() === '' ? null : live,
      billable: values.billable,
    });
    // Fired here — synchronously, right after kicking off the mutation —
    // rather than from the mutation's `onSuccess`. `useStartTimer`'s
    // optimistic update flips `running` truthy once it lands, which can
    // unmount *this* component before the real network response arrives
    // (the plain empty-state usage renders only while `!running`); a
    // callback bound to that unmounted render's `mutate()` call would
    // silently never fire. Calling it here, before that update lands,
    // sidesteps the race — consistent with the rest of this mutation being
    // optimistic (the toast/rollback on actual failure already happens
    // independently, inside `useStartTimer`).
    onStarted?.();
  };

  return (
    <div className="border-b px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
          <span className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
            New timer
          </span>
        </div>
        <span className="font-mono font-semibold text-lg tabular-nums text-muted-foreground">
          {formatElapsed(0)}
        </span>
      </div>
      <TimerForm
        values={values}
        workDate={workDate}
        canEdit
        busy={start.isPending}
        onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
        guard={guard}
        engagement={engagement}
      />
      <Separator className="my-3" />
      <div className="flex items-center justify-end gap-1">
        {onCancel ? (
          <Button variant="outline" onClick={onCancel} disabled={start.isPending}>
            Cancel
          </Button>
        ) : null}
        <Button
          variant="default"
          size="sm"
          onClick={onStart}
          disabled={start.isPending}
          aria-label="Start timer"
        >
          <Play />
          Start Timer
        </Button>
      </div>
    </div>
  );
}

function RunningTimerCard({ view, canEdit }: { view: TimerView; canEdit: boolean }) {
  // Base elapsed is the server total at fetch time; the live count adds
  // the wall-clock since that snapshot so the card ticks without
  // re-polling. `useLiveElapsed` handles null baseAt.
  const baseAt = useTimersFetchedAt();
  const elapsed = useLiveElapsed(view.elapsedSeconds, baseAt, true);
  const isTemp = isTempTimerId(view.timer.id);
  const guard = useRequiredFieldsGuard(
    view.timer.customerId,
    view.timer.productId,
    view.timer.description,
    view.timer.engagementId,
  );
  const engagement = useTimerEngagement(view.timer, view.timer.workDate);
  const patch = usePatchTimer();
  return (
    <div className="border-b px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PulsingDot size="md" />
          <span className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
            {isTemp ? 'Starting…' : 'Running'}
          </span>
        </div>
        <span className="font-mono font-semibold text-lg tabular-nums">
          {formatElapsed(elapsed)}
        </span>
      </div>
      <TimerForm
        values={{
          customerId: view.timer.customerId,
          productId: view.timer.productId,
          engagementId: view.timer.engagementId,
          description: view.timer.description,
          billable: view.timer.billable,
        }}
        workDate={view.timer.workDate}
        canEdit={canEdit && !isTemp}
        busy={patch.isPending}
        onChange={(input) => patch.mutate({ id: view.timer.id, input })}
        guard={guard}
        engagement={engagement}
      />
      <Separator className="my-3" />
      <TimerActions
        view={view}
        canEdit={canEdit && !isTemp}
        running
        elapsedSeconds={elapsed}
        guard={guard}
        needsEngagement={engagement.needsPick}
        onMissingFields={(missing) => applyMissingFieldErrors(guard, missing)}
      />
    </div>
  );
}

function PausedTimerCard({ view, canEdit }: { view: TimerView; canEdit: boolean }) {
  // Scannable-by-default: the summary line names the customer + product
  // + a description preview so a user with many paused timers can find
  // the one they want at a glance. Action buttons stay always-visible
  // (icon-only) and click-to-expand reveals the editable form.
  const [expanded, setExpanded] = useState(false);
  const guard = useRequiredFieldsGuard(
    view.timer.customerId,
    view.timer.productId,
    view.timer.description,
    view.timer.engagementId,
  );
  const engagement = useTimerEngagement(view.timer, view.timer.workDate);
  const patch = usePatchTimer();
  const customerName = useCustomerNameLookup(view.timer.customerId);
  const productName = useProductNameLookup(view.timer.productId);
  return (
    <div className="border-b px-4 py-3">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="-mx-1 flex w-full items-start justify-between gap-3 rounded px-1 py-0.5 text-left hover:bg-accent/40"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40" />
            <div className="min-w-0 flex-1">
              <div className="break-words text-sm font-medium">
                {customerName ? (
                  customerName
                ) : view.timer.customerId ? (
                  <span className="text-muted-foreground italic">Loading…</span>
                ) : (
                  <span className="text-muted-foreground italic">No customer</span>
                )}
              </div>
              {productName ? (
                <div className="break-words text-sm text-muted-foreground">{productName}</div>
              ) : null}
            </div>
          </div>
          {view.timer.description ? (
            <div className="ml-4 break-words text-xs text-muted-foreground">
              {view.timer.description}
            </div>
          ) : null}
          <div className="ml-4 mt-0.5 text-[11px] text-muted-foreground">
            Paused{staleHint(view.timer.workDate)}
          </div>
        </div>
        <span className="font-mono text-sm tabular-nums text-muted-foreground">
          {formatElapsed(view.elapsedSeconds)}
        </span>
      </button>
      {expanded ? (
        <>
          <Separator className="my-3" />
          <TimerForm
            values={{
              customerId: view.timer.customerId,
              productId: view.timer.productId,
              engagementId: view.timer.engagementId,
              description: view.timer.description,
              billable: view.timer.billable,
            }}
            workDate={view.timer.workDate}
            canEdit={canEdit}
            busy={patch.isPending}
            onChange={(input) => patch.mutate({ id: view.timer.id, input })}
            guard={guard}
            engagement={engagement}
          />
        </>
      ) : null}
      <Separator className="my-3" />
      <TimerActions
        view={view}
        canEdit={canEdit}
        running={false}
        elapsedSeconds={view.elapsedSeconds}
        guard={guard}
        needsEngagement={engagement.needsPick}
        onMissingFields={(missing) => {
          // Reveal the form so the (now-errored) field(s) are visible, then
          // focus notes once the textarea has mounted (Customer/Activity use
          // the shared combobox, which has no focus handle to steal).
          setExpanded(true);
          applyMissingFieldErrors(guard, missing, { deferFocus: true });
        }}
      />
    </div>
  );
}

/** A timer's editable fields, independent of whether it's already been
 *  created (`RunningTimerCard`/`PausedTimerCard`, patched on change) or is
 *  still a `DraftTimerCard` (local state, sent once on start). */
interface TimerFieldValues {
  customerId: string | null;
  productId: string | null;
  /** The engagement picked when several cover the timer; null otherwise. */
  engagementId: string | null;
  description: string | null;
  billable: boolean;
}

/** A partial update to `TimerFieldValues` — shares its shape with
 *  `PatchTimerInput`/`CreateTimerInput` so `TimerForm`'s `onChange` maps
 *  directly onto either a `usePatchTimer` call or a draft-state merge. */
type TimerFieldsPatch = Partial<TimerFieldValues>;

/**
 * Customer/activity/notes/billable form shared by an existing timer
 * (`RunningTimerCard`/`PausedTimerCard`, which patch the real timer on every
 * change) and `DraftTimerCard` (no timer yet — changes just update local
 * draft state until "Start timer" is clicked). The form itself doesn't know
 * or care which: it reads `values`, computes the same billable-follows-
 * customer / notes-auto-fill business logic either way, and hands the
 * result to `onChange` — the caller decides how to persist it.
 */
function TimerForm({
  values,
  workDate,
  canEdit,
  busy,
  onChange,
  guard,
  engagement,
}: {
  values: TimerFieldValues;
  workDate: string;
  canEdit: boolean;
  // A save (patch or start) is in flight — disables the two pickers/notes
  // (mirrors the old `patch.isPending`) and feeds the loading spinners.
  busy: boolean;
  onChange: (patch: TimerFieldsPatch) => void;
  guard: RequiredFieldsGuard;
  engagement: TimerEngagement;
}) {
  const { user } = useMe();
  // Pins a manual billable toggle so a later customer change won't re-derive it
  // — mirrors the timesheet day-list's `billableTouched`. A ref (not state)
  // because it only guards the next change, never renders. Resets when the form
  // remounts, which is fine: customer changes only happen while it's mounted.
  const billableTouched = useRef(false);
  const onBillable = () => {
    billableTouched.current = true;
    onChange({ billable: !values.billable });
  };
  // The activity the user picked in THIS form, kept whole. `values` only
  // carries the id, but the customer-change check below needs the full row —
  // and reading it from here means the common path (pick activity → change
  // customer) never waits on, or depends on, a network round-trip. Stale ids
  // are ignored: the reader checks `id === values.productId` first.
  const pickedProduct = useRef<Product | null>(null);
  // Set when the auto-fill below seeded the notes, so a later clear can tell
  // "seeded from the activity we're about to drop" from "typed by the user"
  // and only discard the former.
  const autoFilledDescription = useRef<string | null>(null);
  // A customer or activity change that leaves several engagements covering
  // the timer, held UNSAVED while the user is asked which one. The change and
  // its answer are saved together; Cancel drops the change. Mirrors the
  // timesheet grid: a pick answers the old pair's question, so every change
  // that moves the pair either carries a new answer or clears the old one.
  const [pendingChange, setPendingChange] = useState<TimerFieldsPatch | null>(null);
  const commitPairChange = (patch: TimerFieldsPatch) => {
    const nextCustomerId = patch.customerId !== undefined ? patch.customerId : values.customerId;
    const nextProductId = patch.productId !== undefined ? patch.productId : values.productId;
    if (engagement.resolve(nextCustomerId, nextProductId, null).kind === 'ask') {
      setPendingChange(patch);
      guard.setEngagementPromptOpen(true);
      return;
    }
    onChange({ ...patch, engagementId: null });
  };
  const onProduct = (p: Product | null) => {
    pickedProduct.current = p;
    const patch: TimerFieldsPatch = { productId: p?.id ?? null };
    // Auto-fill the notes from the picked activity's description, but only
    // when the user hasn't written their own yet — never clobber typed notes.
    // Mirrors the timesheet grid, which seeds a new entry's description from
    // the product. The textarea below is keyed on the description so the
    // seeded value actually appears.
    if (p?.description && (values.description ?? '').trim() === '') {
      patch.description = p.description;
      autoFilledDescription.current = p.description;
    }
    commitPairChange(patch);
  };
  const onDescriptionBlur = (value: string) => {
    if (value === (values.description ?? '')) return;
    onChange({ description: value === '' ? null : value });
  };

  const customerId = values.customerId;
  // Resolve the complete selected activity, not just its label. Customer
  // changes must re-check custom ownership, internal/external compatibility,
  // and role visibility before retaining it. This is the fallback path, for a
  // timer restored from the server whose activity this form never saw picked
  // (an already-running or paused timer). It shares the same detail-key cache
  // as ProductAutocomplete, so a rendered selection costs no extra request.
  const selectedActivityQuery = useQuery({
    queryKey: values.productId
      ? queryKeys.products.detail(values.productId)
      : ['products', 'detail', 'none'],
    queryFn: () => productsApi.get(values.productId as string),
    enabled: values.productId != null,
    staleTime: 60_000,
  });
  // Prefer the row the user picked in this form: it is already whole and
  // needs no request, so the pick → change-customer path can't be decided on
  // missing data. Guarded on the id so a stale ref is never read.
  const selectedActivity =
    values.productId != null && pickedProduct.current?.id === values.productId
      ? pickedProduct.current
      : (selectedActivityQuery.data?.data ?? null);
  // Timers are personal, so the in-scope set is always for the session user.
  // The hook returns every team customer's set keyed by id; we index out the
  // one this timer is on below.
  const { byCustomer, isLoaded } = useInScopeProductsByCustomer(user?.id, workDate, workDate);
  // `undefined` (not an empty Set) while loading — an empty set is read
  // by ProductAutocomplete as "fetched, nothing covers this customer"
  // and would push every product under "Out of scope" mid-flight.
  const inScopeSet = customerId && isLoaded ? byCustomer.get(customerId) : undefined;
  // Recents for the picker's "used before by you" band, scoped to this
  // timer's single (customer, day).
  const { byCustomer: recentByCustomer } = useRecentProductsByCustomer(
    user?.id,
    workDate,
    workDate,
  );
  const recentSet = customerId ? recentByCustomer.get(customerId) : undefined;

  // Per-(user, workDate) team memberships. Drives BOTH the customer
  // dropdown scope and the per-customer role scoping for the product
  // picker. Without the role scoping the product picker would show the
  // entire tenant catalog and TimerService.commit (which routes through
  // TimesheetService.create) would 422 on a product the user can't log
  // against under their per-customer role. Mirrors the timesheet pickers;
  // same single-day query is deduped across all open timers on the same
  // workDate. Enabled regardless of customerId so the customer dropdown
  // is scoped before a customer is even picked.
  const membershipsQuery = useQuery({
    queryKey: queryKeys.customerTeam.forUser({
      teamUserId: user?.id ?? '',
      fromDate: workDate,
      toDate: workDate,
    }),
    queryFn: () =>
      customerTeamApi.listForUser({
        teamUserId: user?.id as string,
        fromDate: workDate,
        toDate: workDate,
      }),
    // `workDate` is empty on an optimistic placeholder until the real timer
    // swaps in; the server 400s on an empty fromDate/toDate, so hold the
    // request until the date is real.
    enabled: Boolean(user?.id) && Boolean(workDate),
    staleTime: 60_000,
  });
  // `membershipsQuery.data` is referentially stable across renders, so
  // these memos only recompute when the membership set actually changes.
  const memberships = membershipsQuery.data?.data;

  // Scope the customer dropdown to the customers the user is on the team
  // of for this workDate — same source the timesheet UI uses, so the two
  // pickers stay in lockstep. Resolve the unique ids to full Customer
  // rows in a single batched call.
  const teamCustomerIds = useMemo(
    () => Array.from(new Set((memberships ?? []).map((m) => m.customerId))),
    [memberships],
  );
  const teamCustomersQuery = useQuery({
    queryKey: queryKeys.customers.byIds(teamCustomerIds),
    queryFn: () => customersApi.findByIds(teamCustomerIds),
    enabled: teamCustomerIds.length > 0,
    staleTime: 60_000,
  });
  const teamCustomers = teamCustomersQuery.data?.data ?? EMPTY_CUSTOMERS;

  // The dropdown only OFFERS active customers (server-filtered) so a timer
  // can't be started against a deactivated one. `teamCustomers` above stays
  // all-statuses so a timer already pointing at a since-deactivated customer
  // keeps showing its name.
  const selectableCustomersQuery = useQuery({
    queryKey: queryKeys.customers.activeByIds(teamCustomerIds),
    queryFn: () => customersApi.findActiveByIds(teamCustomerIds),
    enabled: teamCustomerIds.length > 0,
    staleTime: 60_000,
  });
  const selectableCustomers = selectableCustomersQuery.data?.data ?? EMPTY_CUSTOMERS;

  // Find the membership covering (customerId, workDate). When none
  // covers — e.g. the user picked a customer they're not on the team
  // of, or the membership ended — fall back to the user's global role
  // so the picker still scopes meaningfully (an empty array would
  // surface the unfiltered catalog). The actual write gate is on the
  // server's TimesheetService.create.
  const roleIdsForCustomer = useCallback(
    (forCustomerId: string | null): readonly string[] | undefined => {
      if (!forCustomerId) return undefined;
      // Multi-role: union every covering role so the picker offers the union of
      // activities the user can log under any of their roles on this customer
      // (mirrors use-stable-role-lookup; the server narrows to one role on
      // commit, prompting only when an activity is ambiguous).
      const hits = findCoveringMemberships(memberships ?? [], forCustomerId, workDate);
      if (hits.length > 0) return Array.from(new Set(hits.map((m) => m.serviceRoleId)));
      if (user?.serviceRoleId) return [user.serviceRoleId];
      return undefined;
    },
    [workDate, memberships, user?.serviceRoleId],
  );
  // One lookup, two readers: the picker's role filter (below) and the
  // customer-change retention check (`onCustomer`). They MUST agree — scoping
  // the picker by the global-role fallback while judging retention without it
  // would clear activities the new customer's picker still offers.
  const roleIds = useMemo(() => roleIdsForCustomer(customerId), [roleIdsForCustomer, customerId]);

  const selectedCustomer = customerId
    ? (teamCustomers.find((c) => c.id === customerId) ?? null)
    : null;

  const onCustomer = (c: Customer | null) => {
    const nextCustomerId = c?.id ?? null;
    if (nextCustomerId === customerId) return;

    const patch: TimerFieldsPatch = { customerId: nextCustomerId };

    // Keep the current activity only when it is provably present in the new
    // customer's picker. The visibility helper mirrors the picker/server rules:
    // custom ownership, internal match, and effective role access.
    //
    // When the activity's detail is unknown (a server-restored timer whose
    // detail request is still in flight or failed) this clears, where the
    // timesheet day-list KEEPS. The rule is the same in both; the tie-break
    // differs because the stakes do. Clearing a day-list row also destroys its
    // logged hours and notes — hence that view's confirm dialog — while a timer
    // has no hours and keeps typed notes below, so re-picking costs one click.
    // A timer, meanwhile, carries an invalid pair silently until commit.
    if (values.productId != null) {
      const activityRemainsValid =
        nextCustomerId != null &&
        selectedActivity != null &&
        isActivityVisibleForCustomer(selectedActivity, {
          customerId: nextCustomerId,
          isInternal: c?.isInternal === true,
          coveringRoleIds: roleIdsForCustomer(nextCustomerId) ?? [],
        });
      if (!activityRemainsValid) {
        patch.productId = null;
        // Drop notes we seeded from the activity being dropped — they describe
        // work the timer is no longer against, and `onProduct` won't overwrite
        // them, so they'd otherwise survive the next pick and get committed.
        // Notes the user typed are theirs and stay.
        if (
          autoFilledDescription.current != null &&
          values.description === autoFilledDescription.current
        ) {
          patch.description = null;
          autoFilledDescription.current = null;
        }
        // The field emptying on its own is otherwise unexplained until "Start
        // timer" complains about it. Only claim the activity is unavailable
        // when the rule actually said so — a null `selectedActivity` means its
        // detail never resolved, so the pair was never judged and the activity
        // may well be fine on the new customer.
        toast.info(
          selectedActivity == null
            ? 'Couldn’t check the activity against this customer — it was cleared, please pick it again.'
            : 'Activity isn’t available for this customer — it was cleared.',
        );
      }
    }

    // Billable follows the customer in the same patch — mirrors the timesheet
    // day-list/cell editor. Internal is an invariant (always force off); an
    // external customer only sets the *default* on, so it defers to an explicit
    // user toggle (billableTouched) just like the day list does.
    if (c?.isInternal === true) {
      if (values.billable) patch.billable = false;
    } else if (c != null && !billableTouched.current && !values.billable) {
      patch.billable = true;
    }
    commitPairChange(patch);
  };

  // The engagement question — about the pair a held change would produce, or
  // the timer's own pair when Start/Commit found it unanswered.
  const promptCustomerId =
    pendingChange?.customerId !== undefined ? pendingChange.customerId : customerId;
  const promptProductId =
    pendingChange?.productId !== undefined ? pendingChange.productId : values.productId;
  const promptCandidates = engagement.candidatesFor(promptCustomerId, promptProductId);
  const promptOthers = engagement
    .activeFor(promptCustomerId)
    .filter((e) => !promptCandidates.some((c) => c.engagementId === e.engagementId));
  const promptCustomerName =
    teamCustomers.find((c) => c.id === promptCustomerId)?.name ?? undefined;
  // A held activity pick isn't on `values` yet, so name it from the pick.
  const promptActivityName =
    promptProductId == null
      ? undefined
      : pickedProduct.current?.id === promptProductId
        ? pickedProduct.current.name
        : selectedActivity?.id === promptProductId
          ? selectedActivity.name
          : undefined;
  // What the Engagement row offers for the timer as it stands.
  const currentCandidates = engagement.candidatesFor(customerId, values.productId);
  const engagementShown =
    engagement.resolution.kind === 'bound' || engagement.resolution.kind === 'ask';
  // The timer's engagement does not list its activity — a deliberate pick, or
  // a contract amended since. Recorded as it stands (the server allows it),
  // and the row stays a button so it can be changed…
  const engagementOffList =
    engagement.resolution.kind === 'bound' && !engagement.resolution.engagement.coversActivity;
  // …but only MARKED as out of scope once that labelling is released.
  const engagementOutOfScope = SHOW_OUT_OF_SCOPE && engagementOffList;
  // Same billable enable/disable rule as the timesheet day-list/cell-editor:
  // the switch is off + read-only for internal customers (never billable).
  // The timesheet's other gate — `editable` (period not locked && status
  // editable) — has no timer analog: a live timer is inherently editable, so
  // `canEdit` (permission + not an optimistic placeholder) is the equivalent.
  const customerIsInternal = selectedCustomer?.isInternal === true;

  // Spinner cues for the two pickers:
  //   - Customer options resolve via memberships → the customers batch.
  //   - The Activity catalog's scope (role memberships + in-scope set) must
  //     land once a customer is picked, before the picker is meaningful.
  const customerOptionsLoading = membershipsQuery.isLoading || selectableCustomersQuery.isLoading;
  const activityDataLoading = customerId != null && (membershipsQuery.isLoading || !isLoaded);

  return (
    <div className="space-y-2">
      <FieldRow label="Customer" loading={customerOptionsLoading || busy} required>
        <CustomerAutocomplete
          value={customerId}
          onChange={onCustomer}
          disabled={!canEdit || busy}
          placeholder="Pick customer"
          items={selectableCustomers}
          selectedItem={selectedCustomer}
          className={guard.customerError ? MISSING_FIELD_ERROR_CLASSNAME : undefined}
        />
      </FieldRow>
      {guard.customerError ? (
        <p className="text-[11px] text-destructive">Customer is required.</p>
      ) : null}
      <FieldRow label="Activity" loading={activityDataLoading || busy} required>
        <ProductAutocomplete
          value={values.productId}
          onChange={onProduct}
          disabled={!canEdit || busy || customerId == null}
          placeholder={customerId == null ? 'Pick customer first' : 'Pick activity'}
          // Role scoping applies to the internal customer too (internal then
          // service-role filter); ProductAutocomplete only drops SOW sectioning
          // for it. Pass normal props unconditionally and just flag internal.
          filterByInternal={selectedCustomer?.isInternal}
          // Surface this customer's custom activities alongside the global
          // catalog (and exclude other customers'). Server matches on write.
          customerId={customerId}
          // Withheld while the out-of-scope UI is hidden so the picker drops
          // its "In/Out of scope" sections and the trigger badge. Same flag as
          // the timesheet pickers so all three restore together.
          inScopeProductIds={SHOW_OUT_OF_SCOPE_BADGE ? inScopeSet : undefined}
          filterServiceRoleIds={roleIds}
          recentProductIds={recentSet}
          className={guard.activityError ? MISSING_FIELD_ERROR_CLASSNAME : undefined}
        />
      </FieldRow>
      {guard.activityError ? (
        <p className="text-[11px] text-destructive">Activity is required.</p>
      ) : null}
      {engagementShown ? (
        <FieldRow label="Engagement" required={currentCandidates.length > 1}>
          {currentCandidates.length > 1 || engagementOffList ? (
            // Several cover the timer, or the one it carries is out of scope:
            // the pick is the user's to make (and to change), so the row is a
            // button that re-asks.
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canEdit || busy}
              onClick={() => guard.setEngagementPromptOpen(true)}
              title={engagementOutOfScope ? 'Does not list this activity' : undefined}
              className={cn(
                'w-full justify-start font-normal',
                guard.engagementError && MISSING_FIELD_ERROR_CLASSNAME,
              )}
            >
              {engagement.resolution.kind === 'bound' ? (
                <span className={cn(engagementOutOfScope && 'text-amber-700 dark:text-amber-500')}>
                  {engagement.resolution.engagement.name}
                  {engagementOutOfScope ? <span className="sr-only"> (out of scope)</span> : null}
                </span>
              ) : (
                <span className="text-muted-foreground">Pick engagement</span>
              )}
            </Button>
          ) : engagement.resolution.kind === 'bound' ? (
            // Exactly one covers it — nothing to choose; commit binds it.
            <p className="px-1 text-muted-foreground text-sm">
              {engagement.resolution.engagement.name}
            </p>
          ) : null}
        </FieldRow>
      ) : null}
      {guard.engagementError ? (
        <p className="text-[11px] text-destructive">Engagement is required.</p>
      ) : null}
      <TimesheetEngagementDialog
        open={guard.engagementPromptOpen}
        customerName={promptCustomerName}
        activityName={promptActivityName}
        candidates={promptCandidates}
        others={promptOthers}
        currentEngagementId={pendingChange ? null : values.engagementId}
        onSelect={(engagementId) => {
          onChange({ ...pendingChange, engagementId });
          setPendingChange(null);
          guard.setEngagementPromptOpen(false);
        }}
        // Cancel drops a held change (the timer keeps its old customer and
        // activity); for a prompt raised by Start/Commit it just closes —
        // the action stays blocked until it is answered.
        onDismiss={() => {
          setPendingChange(null);
          guard.setEngagementPromptOpen(false);
        }}
      />
      <FieldRow label="Notes" required>
        <DescriptionInput
          // Remount when the stored description changes (e.g. auto-filled on
          // activity select) so the textarea re-seeds from the new value.
          // Typing doesn't change the stored value until blur, so in-progress
          // edits aren't interrupted.
          key={values.description ?? ''}
          defaultValue={values.description ?? ''}
          onBlur={onDescriptionBlur}
          disabled={!canEdit || busy}
          inputRef={guard.descriptionInputRef}
          error={guard.descriptionError}
          // Track every keystroke so the commit guard (or `DraftTimerCard`'s
          // start handler) reads the live value, and clear the error as soon
          // as the user types something.
          onValueChange={(val) => {
            guard.liveDescriptionRef.current = val;
            if (val.trim() !== '') guard.setDescriptionError(false);
          }}
        />
      </FieldRow>
      <div className="pt-0.5">
        <BillableSwitch
          // Mirrors the timesheet: billable is meaningless until a customer is
          // picked (off + read-only), and internal customers force it off; the
          // committed entry inherits this flag.
          billable={customerIsInternal || customerId == null ? false : values.billable}
          disabled={!canEdit || customerIsInternal || customerId == null}
          onToggle={onBillable}
        />
      </div>
    </div>
  );
}

function FieldRow({
  label,
  required,
  loading,
  children,
}: {
  label: string;
  required?: boolean;
  /** Show a small spinner next to the label while the field's data
   *  (options/scope) loads or a pick is being saved. */
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
        <span>
          {label}
          {required ? <span className="text-destructive"> *</span> : null}
        </span>
        {loading ? <Loader2 className="size-3 animate-spin" aria-label="Loading" /> : null}
      </div>
      {children}
    </div>
  );
}

function DescriptionInput({
  defaultValue,
  onBlur,
  disabled,
  inputRef,
  error,
  onValueChange,
}: {
  defaultValue: string;
  onBlur: (value: string) => void;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  // A hard error raised by a commit attempt with no notes — the field carries
  // no highlighting while simply empty, only once a commit has blocked on it.
  error?: boolean;
  onValueChange?: (value: string) => void;
}) {
  const [value, setValue] = useState(defaultValue);
  // Notes are required to land a complete timesheet entry, alongside customer
  // and activity. A commit attempt with no notes blocks and flips this to a
  // hard error (the timer can still be paused — only the commit is gated,
  // since committing logs time and logged time needs notes).
  return (
    <>
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onValueChange?.(e.target.value);
        }}
        onBlur={(e) => onBlur(e.target.value)}
        disabled={disabled}
        placeholder="What are you working on?"
        rows={2}
        maxLength={4000}
        aria-invalid={error || undefined}
        className={cn(
          'w-full resize-none rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
          error && MISSING_FIELD_ERROR_CLASSNAME,
        )}
      />
      {error ? <p className="text-[11px] text-destructive">Notes is required.</p> : null}
    </>
  );
}

function TimerActions({
  view,
  canEdit,
  running,
  elapsedSeconds,
  guard,
  needsEngagement,
  onMissingFields,
}: {
  view: TimerView;
  canEdit: boolean;
  running: boolean;
  // Live elapsed for a running timer, static server total for a paused one.
  // Drives the commit button: below the round-to-0.01h cutoff there's nothing
  // to commit, so the button stays disabled instead of letting the user click
  // into the server's "nothing to commit" error.
  elapsedSeconds: number;
  guard: RequiredFieldsGuard;
  /** Several engagements cover the timer and none is picked (`useTimerEngagement`). */
  needsEngagement: boolean;
  onMissingFields: (missing: readonly MissingField[]) => void;
}) {
  const pause = usePauseTimer();
  const resume = useResumeTimer();
  const commit = useCommitTimer();
  const patch = usePatchTimer();
  const discard = useDiscardTimer();
  // The server's own refusal, for when the client didn't know an engagement
  // was owed (coverage still loading, or a contract changed while the timer
  // ran). The answer is saved on the timer, then the commit is retried.
  const engagementPrompt = useEngagementRequiredPrompt();
  const busy =
    pause.isPending || resume.isPending || commit.isPending || discard.isPending || patch.isPending;

  // The committed entry's role is the timer activity's own role (resolved
  // server-side from the activity), so there's no role to disambiguate.
  const commitTimer = async () => {
    // All three fields are required to log time — block the commit in place
    // (rather than letting the server 422) and steer the user to whichever
    // are still missing. Customer/activity read straight off `view.timer`:
    // both patch optimistically the instant they're picked, so the cache is
    // already race-free by the time a click can land. Notes reads the live
    // ref instead, since notes only persist on blur (see `useRequiredFieldsGuard`).
    const live = guard.liveDescriptionRef.current;
    const missing = computeMissingFields(
      view.timer.customerId,
      view.timer.productId,
      live,
      needsEngagement,
    );
    if (missing.length > 0) {
      onMissingFields(missing);
      return;
    }
    // If the notes were just typed and the blur hasn't persisted them yet,
    // save them first so the server sees the description when commit creates
    // the timesheet row. A failed save already toasts — don't commit on it.
    if (live !== (view.timer.description ?? '')) {
      try {
        await patch.mutateAsync({ id: view.timer.id, input: { description: live } });
      } catch {
        return;
      }
    }
    commit.mutate(
      { id: view.timer.id },
      {
        onError: (err) => {
          const candidates = engagementRequiredCandidates(err);
          if (!candidates || candidates.length === 0) return;
          engagementPrompt.ask({
            candidates,
            customerId: view.timer.customerId,
            productId: view.timer.productId,
            onPick: async (engagementId) => {
              try {
                await patch.mutateAsync({ id: view.timer.id, input: { engagementId } });
              } catch {
                return; // the failed save already toasted
              }
              commit.mutate({ id: view.timer.id });
            },
            onCancel: () => {},
          });
        },
      },
    );
  };
  // Mirrors the server commit guard: under ~18s the elapsed rounds to 0.00h and
  // there's nothing to log. Gate the button on it so committing too-soon is
  // impossible rather than an after-the-fact toast.
  const nothingToCommit = !isCommittableElapsed(elapsedSeconds);
  // Customer/activity/notes no longer hard-disable the button — a click with
  // any of them missing is validated (and surfaced) the same way as notes
  // always was, via `commitTimer` above.
  const commitDisabled = !canEdit || busy || nothingToCommit;
  const commitTitle = nothingToCommit
    ? 'Not enough time tracked yet to commit'
    : 'Commit to timesheet';

  return (
    <div className="flex items-center justify-end gap-1">
      {running ? (
        <IconTooltip label="Pause">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => pause.mutate(view.timer.id)}
            disabled={!canEdit || busy}
            aria-label="Pause"
          >
            <Pause className="size-3.5" />
          </Button>
        </IconTooltip>
      ) : (
        <IconTooltip label="Resume">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => resume.mutate(view.timer.id)}
            disabled={!canEdit || busy}
            aria-label="Resume"
          >
            <Play className="size-3.5" />
          </Button>
        </IconTooltip>
      )}
      <IconTooltip label={commitTitle}>
        {/* A disabled <button> emits no pointer events, so the tooltip would
            never fire on hover. Wrap it in a span that carries the hover (the
            disabled button sets pointer-events:none, so the pointer falls
            through to the span) — this is what surfaces *why* commit is off. */}
        <span className={cn('inline-flex', commitDisabled && 'cursor-not-allowed')}>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 disabled:cursor-not-allowed"
            onClick={() => commitTimer()}
            disabled={commitDisabled}
            aria-label="Commit to timesheet"
          >
            <Check className="size-3.5" />
          </Button>
        </span>
      </IconTooltip>
      <DiscardButton timerId={view.timer.id} disabled={!canEdit || busy} />
      {engagementPrompt.host}
    </div>
  );
}

/**
 * Discard with an explicit confirmation dialog. The trash button opens an
 * "Are you sure?" prompt; only the dialog's Discard button destroys the
 * timer. A modal dialog coexists with the surrounding (non-modal) timer
 * popover.
 */
function DiscardButton({ timerId, disabled }: { timerId: string; disabled: boolean }) {
  const discard = useDiscardTimer();
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconTooltip label="Discard">
        <Button
          size="icon"
          variant="ghost"
          className="size-7"
          onClick={() => setOpen(true)}
          disabled={disabled}
          aria-label="Discard"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </IconTooltip>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Discard this timer?</DialogTitle>
            <DialogDescription>
              The accumulated time will be permanently lost. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setOpen(false);
                discard.mutate(timerId);
              }}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function IconTooltip({ label, children }: { label: string; children: React.ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

/** Hint shown for paused timers with a stale work_date. */
function staleHint(workDate: string): string {
  const today = new Date().toISOString().slice(0, 10);
  if (workDate >= today) return '';
  const ageMs = Date.parse(`${today}T00:00:00Z`) - Date.parse(`${workDate}T00:00:00Z`);
  const days = Math.max(1, Math.floor(ageMs / (1000 * 60 * 60 * 24)));
  return ` · ${days}d ago`;
}
